<!--
  Pick a mark. Twenty-four of them, and never an upload.

  The set comes from `GET /api/avatars` when it answers and from `AVATARS` in
  `protocol.ts` when it does not, so a picker on a slow connection is a picker, not a
  spinner. Anything the server offers that this build cannot draw is left out rather
  than shown as an empty tile — `hasMark()` is the filter, and the test in
  `glyphs.test.ts` is what stops the two lists drifting apart quietly.
-->
<script lang="ts">
  import AvatarMark from './AvatarMark.svelte';
  import { hasMark } from './glyphs';
  import { AVATARS, type AvatarId } from './protocol';

  interface Props {
    value: AvatarId | null;
    /** The account colour, so the marks are previewed as they will actually look. */
    color?: string;
    /** The set the server published, if it has been fetched. */
    options?: readonly AvatarId[] | null;
    disabled?: boolean;
    onpick: (avatar: AvatarId) => void;
  }

  let { value, color = 'var(--accent)', options = null, disabled = false, onpick }: Props = $props();

  const list = $derived((options ?? AVATARS).filter((a) => hasMark(a)));
</script>

<div class="picker" role="radiogroup" aria-label="Your avatar">
  {#each list as avatar (avatar)}
    <button
      type="button"
      role="radio"
      aria-checked={avatar === value}
      aria-label={avatar}
      {disabled}
      class:on={avatar === value}
      onclick={() => onpick(avatar)}
    >
      <AvatarMark {avatar} {color} size={72} selected={avatar === value} square />
      <span>{avatar}</span>
    </button>
  {/each}
</div>

<style>
  .picker {
    display: grid;
    /* Large portrait targets flow into the available profile-panel width. */
    grid-template-columns: repeat(auto-fill, minmax(86px, 1fr));
    gap: var(--space-2);
    margin-top: var(--space-3);
  }

  button {
    display: grid;
    justify-items: center;
    gap: 5px;
    padding: var(--space-2);
    border: 1px solid transparent;
    border-radius: var(--radius);
    background: transparent;
    color: var(--text-faint);
    transition: background-color var(--dur-fast) var(--ease);
  }
  button:hover:not(:disabled) {
    background: var(--surface-1);
    color: var(--text-dim);
  }
  button:disabled {
    opacity: 0.5;
    cursor: default;
  }
  button.on {
    border-color: var(--focus-ring);
    background: var(--surface-1);
    color: var(--text);
  }
  button:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }

  span {
    font-size: var(--text-2xs);
    letter-spacing: var(--tracking-wide);
    /* Twenty-four short words at 11px: no wrapping, no ellipsis needed, but clip
       rather than push the grid wider if a future id is long. */
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
</style>
